import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Circle, useMapEvents } from 'react-leaflet';
import { Box, Slider, Typography } from '@mui/material';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import CircularProgress from '@mui/material/CircularProgress';

// Fix for default marker icon
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

interface LocationPickerProps {
  initialLocation?: { lat: number; lng: number; radius: number };
  onSave: (lat: number, lng: number, radius: number) => Promise<void>;
  saving: boolean;
}

function MapClickHandler({ onLocationSelect }: { onLocationSelect: (lat: number, lng: number) => void }) {
  useMapEvents({ click: (e) => onLocationSelect(e.latlng.lat, e.latlng.lng) });
  return null;
}

const LocationPicker: React.FC<LocationPickerProps> = ({ initialLocation, onSave, saving }) => {
  const [position, setPosition] = useState<[number, number] | null>(
    initialLocation ? [initialLocation.lat, initialLocation.lng] : null
  );
  const [radius, setRadius] = useState(initialLocation?.radius || 10);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (!initialLocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setPosition([pos.coords.latitude, pos.coords.longitude]),
        () => setPosition([51.505, -0.09]),
      );
    }
  }, [initialLocation]);

  const handleLocationSelect = (lat: number, lng: number) => {
    setPosition([lat, lng]);
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (position) {
      await onSave(position[0], position[1], radius);
      setHasChanges(false);
    }
  };

  if (!position) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
        <CircularProgress size={14} />
        <Typography sx={{ fontFamily: "'DM Sans'", fontSize: '0.7rem', color: 'text.disabled' }}>Locating…</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Map */}
      <Box sx={{ borderRadius: '2px', overflow: 'hidden', border: '1px solid', borderColor: 'divider', mb: 1.5 }}>
        <MapContainer
          center={position}
          zoom={13}
          style={{ height: '200px', width: '100%' }}
          zoomControl={false}
        >
          <TileLayer
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
            attribution='&copy; <a href="https://carto.com">CARTO</a>'
          />
          <MapClickHandler onLocationSelect={handleLocationSelect} />
          <Marker position={position} />
          <Circle
            center={position}
            radius={radius * 1000}
            pathOptions={{ color: '#c0392b', fillColor: '#c0392b', fillOpacity: 0.1, weight: 1 }}
          />
        </MapContainer>
      </Box>

      {/* Controls row: label + slider + radius + save */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <MyLocationIcon sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />
        <Slider
          value={radius}
          onChange={(_, v) => { setRadius(v as number); setHasChanges(true); }}
          min={1}
          max={100}
          sx={{
            flex: 1, color: 'primary.main', height: 3,
            '& .MuiSlider-thumb': { width: 12, height: 12 },
            '& .MuiSlider-rail': { backgroundColor: 'divider' },
          }}
        />
        <Typography sx={{ fontFamily: "'DM Sans'", fontSize: '0.68rem', color: 'text.secondary', flexShrink: 0, minWidth: 36, textAlign: 'right' }}>
          {radius} km
        </Typography>
        {hasChanges && (
          <Box
            onClick={saving ? undefined : handleSave}
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.3,
              px: 1, py: 0.3, cursor: saving ? 'default' : 'pointer',
              border: '1px solid', borderColor: 'primary.main',
              borderRadius: '2px', color: 'primary.main',
              transition: 'all 0.2s',
              '&:hover': { backgroundColor: 'rgba(192,57,43,0.04)' },
            }}
          >
            {saving ? <CircularProgress size={10} sx={{ color: 'primary.main' }} /> : <SaveOutlinedIcon sx={{ fontSize: 12 }} />}
            <Typography sx={{ fontFamily: "'DM Sans'", fontSize: '0.6rem', fontWeight: 500 }}>Save</Typography>
          </Box>
        )}
      </Box>

      {/* Hint */}
      {!initialLocation && (
        <Typography sx={{ fontFamily: "'DM Sans'", fontSize: '0.6rem', color: 'text.disabled', mt: 0.5 }}>
          Tap the map to set your location
        </Typography>
      )}
    </Box>
  );
};

export default LocationPicker;
